import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, Globe } from 'lucide-react';

const REGIONS = ['US', 'GB', 'IN', 'GLOBAL'];
const SECTIONS = [
  { key: 'news', label: 'News', color: '#EF4444' },
  { key: 'investor', label: 'Investor', color: '#3B82F6' },
  { key: 'reseller', label: 'Reseller', color: '#F97316' },
];

// Combinations that are expected to be empty by design — don't flag them as warnings.
const EXPECTED_EMPTY = new Set([
  'GLOBAL:news',     // News is always region-tagged, never GLOBAL
]);

function thresholdFor(region, section) {
  // Minimum healthy count per (region, section) combo
  if (section === 'news') return { warn: 30, healthy: 50 };
  if (region === 'GLOBAL' && section !== 'news') return { warn: 20, healthy: 40 };
  if (region === 'US' && section !== 'news') return { warn: 15, healthy: 30 };
  if (region === 'GB') return { warn: 5, healthy: 15 };
  if (region === 'IN') return { warn: 5, healthy: 15 };
  return { warn: 1, healthy: 5 };
}

function statusFor(count, region, section) {
  const key = `${region}:${section}`;
  if (EXPECTED_EMPTY.has(key)) return 'na';
  const { warn, healthy } = thresholdFor(region, section);
  if (count === 0) return 'critical';
  if (count < warn) return 'warn';
  if (count < healthy) return 'ok';
  return 'healthy';
}

const STATUS_STYLE = {
  healthy:  { bg: 'bg-green-500/15',  border: 'border-green-500/30',  text: 'text-green-400',  label: 'Healthy' },
  ok:       { bg: 'bg-blue-500/15',   border: 'border-blue-500/30',   text: 'text-blue-400',   label: 'OK' },
  warn:     { bg: 'bg-amber-500/15',  border: 'border-amber-500/30',  text: 'text-amber-400',  label: 'Low' },
  critical: { bg: 'bg-red-500/15',    border: 'border-red-500/30',    text: 'text-red-400',    label: 'Empty' },
  na:       { bg: 'bg-white/5',       border: 'border-white/10',      text: 'text-gray-500',   label: '—' },
};

export function RegionCoverage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('wtf_admin_token');
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/volumetrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(await resp.json());
      } catch (e) {
        console.error('Region coverage load failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-5 flex items-center gap-3">
        <Loader2 className="animate-spin text-gray-400" size={16} />
        <span className="text-sm text-gray-400">Loading region coverage...</span>
      </div>
    );
  }
  if (!data) return null;

  // Count warnings / critical
  const cells = [];
  for (const region of REGIONS) {
    const totals = data.by_region?.[region]?.totals || {};
    for (const section of SECTIONS) {
      const count = totals[section.key] || 0;
      const status = statusFor(count, region, section.key);
      cells.push({ region, section: section.key, count, status });
    }
  }
  const criticalCount = cells.filter(c => c.status === 'critical').length;
  const warnCount = cells.filter(c => c.status === 'warn').length;
  const issues = cells.filter(c => c.status === 'critical' || c.status === 'warn');

  return (
    <div className="rounded-2xl border border-white/10 bg-[#111827] p-5" data-testid="region-coverage">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-gray-400" />
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Region Coverage Health</h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {criticalCount === 0 && warnCount === 0 ? (
            <span className="flex items-center gap-1.5 text-green-400 font-bold">
              <CheckCircle2 size={14} /> All regions healthy
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-amber-400 font-bold">
              <AlertTriangle size={14} />
              {criticalCount > 0 && `${criticalCount} empty`}
              {criticalCount > 0 && warnCount > 0 && ' · '}
              {warnCount > 0 && `${warnCount} low`}
            </span>
          )}
        </div>
      </div>

      {/* Grid: rows = regions, cols = sections */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left py-2 px-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Region</th>
              {SECTIONS.map(s => (
                <th key={s.key} className="text-center py-2 px-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: s.color }}>
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REGIONS.map(region => (
              <tr key={region} className="border-t border-white/5">
                <td className="py-2 px-2 font-black text-white">{region}</td>
                {SECTIONS.map(s => {
                  const cell = cells.find(c => c.region === region && c.section === s.key);
                  const style = STATUS_STYLE[cell.status];
                  return (
                    <td key={s.key} className="py-2 px-2">
                      <div className={`${style.bg} ${style.border} border rounded-lg px-2 py-1.5 text-center`}>
                        <div className={`text-sm font-black ${style.text}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {cell.count}
                        </div>
                        <div className={`text-[9px] font-bold ${style.text} opacity-80`}>
                          {style.label}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Issue list */}
      {issues.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Action items</p>
          {issues.map((c, i) => (
            <p key={i} className="text-xs text-gray-400">
              <span className={STATUS_STYLE[c.status].text + ' font-bold'}>
                {c.status === 'critical' ? '●' : '◐'} {c.region} / {SECTIONS.find(s => s.key === c.section).label}:
              </span>{' '}
              {c.count} records{c.status === 'critical' ? ' — ingest may be failing' : ' — below healthy threshold'}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
